import { useState } from 'react';
import { moneyShort } from '../../lib/format';
import { usd } from './shared';

interface Point {
  key: string;
  label: string;
  actual: number | null;
  projected: number | null;
}

const W = 720;
const H = 280;
const PAD = { top: 16, right: 16, bottom: 34, left: 56 };

/** Operating cash: solid for approved months, dashed for the forecast, with the board minimum as a line. */
export function CashForecastChart({
  points,
  threshold,
}: {
  points: Point[];
  threshold: number | null;
}) {
  const [active, setActive] = useState<number | null>(null);
  if (points.length === 0) return null;
  const values = points.flatMap((p) => [p.actual, p.projected, threshold]).filter((v): v is number => v !== null);
  const max = Math.max(...values, 1) * 1.12;
  const min = Math.min(0, ...values);
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (points.length === 1 ? plotW / 2 : (i * plotW) / (points.length - 1));
  const y = (v: number) => PAD.top + plotH - ((v - min) / (max - min || 1)) * plotH;
  const line = (series: Array<number | null>) =>
    series
      .map((v, i) => (v === null ? null : `${i === series.findIndex((x) => x !== null) ? 'M' : 'L'} ${x(i)} ${y(v)}`))
      .filter(Boolean)
      .join(' ');
  const hovered = active === null ? null : points[active];
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => min + (max - min) * f);

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label="Operating cash by month, actual then forecast"
        style={{ display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setActive(null)}
      >
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke="var(--bd)" />
            <text x={PAD.left - 8} y={y(v) + 4} fontSize="10" textAnchor="end" fill="var(--mut)">{moneyShort(v)}</text>
          </g>
        ))}
        {min < 0 && <line x1={PAD.left} x2={W - PAD.right} y1={y(0)} y2={y(0)} stroke="var(--mid)" />}
        {threshold !== null && (
          <line x1={PAD.left} x2={W - PAD.right} y1={y(threshold)} y2={y(threshold)} stroke="var(--warn)" strokeDasharray="4 4" />
        )}
        <path d={line(points.map((p) => p.actual))} fill="none" stroke="var(--ok)" strokeWidth="2.5" />
        <path d={line(points.map((p) => p.projected))} fill="none" stroke="var(--info)" strokeWidth="2.5" strokeDasharray="6 4" />
        {points.map((p, i) => (
          <g key={p.key} onMouseEnter={() => setActive(i)} tabIndex={0} onFocus={() => setActive(i)} onBlur={() => setActive(null)}>
            <rect x={x(i) - 12} y={PAD.top} width={24} height={plotH} fill="transparent" />
            {p.actual !== null && <circle cx={x(i)} cy={y(p.actual)} r="3.5" fill="var(--ok)" />}
            {p.projected !== null && <circle cx={x(i)} cy={y(p.projected)} r="3.5" fill="var(--info)" />}
            {(i === 0 || i === points.length - 1 || i % Math.ceil(points.length / 8) === 0) && (
              <text x={x(i)} y={H - 12} fontSize="10" textAnchor="middle" fill="var(--mut)">{p.label}</text>
            )}
          </g>
        ))}
      </svg>
      <div className="row" style={{ gap: 16, marginTop: 6 }} aria-hidden="true">
        <span className="mut"><span style={{ color: 'var(--ok)' }}>●</span> Actual (approved)</span>
        <span className="mut"><span style={{ color: 'var(--info)' }}>●</span> Forecast</span>
        {threshold !== null && <span className="mut"><span style={{ color: 'var(--warn)' }}>— —</span> Board minimum</span>}
      </div>
      {hovered && (
        <div className="card tight" role="status" style={{ position: 'absolute', top: 4, right: 4, minWidth: 180, boxShadow: 'var(--sh)' }}>
          <strong>{hovered.label}</strong>
          {hovered.actual !== null && <div className="row between"><span className="mut">Actual</span><span>{usd(hovered.actual)}</span></div>}
          {hovered.projected !== null && <div className="row between"><span className="mut">Forecast</span><span>{usd(hovered.projected)}</span></div>}
        </div>
      )}
    </div>
  );
}
